import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Upload, X, Save, GripVertical } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";

const ROLES = [
  { id: "super_admin", label: "Super Admin" },
  { id: "store_manager", label: "Store Manager" },
  { id: "fulfillment_staff", label: "Fulfillment Staff" },
];

const imgSrc = (img) =>
  img && (img.startsWith("/api/") || img.startsWith("http")) ? mediaUrl(img) : img;

const blank = () => ({ label: "", route: "", roles: ["super_admin"], summary: "", steps: [""], tips: [""], image: "" });

function ListField({ title, items, onChange, testId }) {
  return (
    <div>
      <label className="adm-label">{title}</label>
      <div className="space-y-2">
        {items.map((v, i) => (
          <div key={i} className="flex gap-2 items-start">
            <span className="font-body text-[0.65rem] text-ink/40 mt-2.5 w-4 text-right">{i + 1}</span>
            <textarea
              rows={2}
              className="adm-input flex-1 resize-y"
              value={v}
              onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))}
              data-testid={`${testId}-${i}`}
            />
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))}
              className="text-ink/40 hover:text-destructive mt-2" data-testid={`${testId}-remove-${i}`}>
              <X size={15} />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onChange([...items, ""])}
        className="mt-2 inline-flex items-center gap-1 font-body text-[0.6rem] uppercase tracking-wide2 text-ink/60 hover:text-ink" data-testid={`${testId}-add`}>
        <Plus size={12} /> Add {title.replace(/s$/, "").toLowerCase()}
      </button>
    </div>
  );
}

export default function AdminGuideEditor() {
  const [sections, setSections] = useState([]);
  const [selId, setSelId] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/guide/sections");
    setSections(data);
    return data;
  };

  useEffect(() => { load(); }, []);

  const select = (s) => { setSelId(s.id); setForm({ ...s, steps: [...(s.steps || [])], tips: [...(s.tips || [])], roles: [...(s.roles || [])] }); };

  const startNew = () => { setSelId("new"); setForm(blank()); };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleRole = (r) => set("roles", form.roles.includes(r) ? form.roles.filter((x) => x !== r) : [...form.roles, r]);

  const uploadImg = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image", data.url);
      toast.success("Screenshot updated");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const save = async () => {
    if (!form.label.trim()) { toast.error("Section title is required"); return; }
    const payload = { ...form, steps: form.steps.filter((s) => s.trim()), tips: form.tips.filter((t) => t.trim()) };
    setSaving(true);
    try {
      if (selId === "new") {
        const { data } = await api.post("/admin/guide/sections", payload);
        const list = await load();
        select(list.find((s) => s.id === data.id) || data);
        toast.success("Section created");
      } else {
        await api.put(`/admin/guide/sections/${selId}`, payload);
        await load();
        toast.success("Saved");
      }
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this guide section?")) return;
    await api.delete(`/admin/guide/sections/${id}`);
    if (selId === id) { setSelId(null); setForm(null); }
    await load();
    toast.success("Deleted");
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[idx], next[j]] = [next[j], next[idx]];
    setSections(next);
    await api.put("/admin/guide/reorder", { ids: next.map((s) => s.id) });
  };

  return (
    <div data-testid="admin-guide-editor">
      <div className="mb-8 flex items-center justify-between gap-4">
        <div>
          <Link to="/admin/guide" className="inline-flex items-center gap-1.5 font-body text-[0.6rem] uppercase tracking-wide2 text-ink/50 hover:text-ink mb-2" data-testid="guide-editor-back">
            <ArrowLeft size={13} /> Back to Guide
          </Link>
          <h1 className="font-heading text-3xl">Guide Editor</h1>
          <p className="font-body text-sm text-ink/50 mt-1">Add, edit, reorder and remove walkthrough sections. Changes appear instantly on the Admin Guide.</p>
        </div>
        <button onClick={startNew} className="flex-none inline-flex items-center gap-2 bg-ink text-linen px-4 py-2.5 font-body text-[0.62rem] uppercase tracking-wide2 hover:bg-ink/90 transition-colors" data-testid="guide-add-section">
          <Plus size={14} /> New Section
        </button>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Section list */}
        <div className="self-start" data-testid="guide-editor-list">
          <p className="font-body uppercase tracking-luxe text-[0.55rem] text-ink/40 px-1 pb-3">Order shown to admins</p>
          <div className="space-y-1">
            {sections.map((s, i) => (
              <div key={s.id} className={`flex items-center gap-1 border px-2 py-2 ${selId === s.id ? "border-ink bg-warm/30" : "border-ink/10 hover:border-ink/30"}`}>
                <GripVertical size={13} className="text-ink/25 flex-none" />
                <button onClick={() => select(s)} className="flex-1 text-left font-body text-[0.75rem] truncate" data-testid={`guide-editor-select-${s.id}`}>
                  {s.label || <span className="text-ink/30 italic">Untitled</span>}
                </button>
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-ink/40 hover:text-ink disabled:opacity-20" data-testid={`guide-editor-up-${s.id}`}><ChevronUp size={14} /></button>
                <button onClick={() => move(i, 1)} disabled={i === sections.length - 1} className="text-ink/40 hover:text-ink disabled:opacity-20" data-testid={`guide-editor-down-${s.id}`}><ChevronDown size={14} /></button>
                <button onClick={() => remove(s.id)} className="text-ink/40 hover:text-destructive" data-testid={`guide-editor-delete-${s.id}`}><Trash2 size={13} /></button>
              </div>
            ))}
            {!sections.length && <p className="px-1 font-body text-xs text-ink/40">No sections yet.</p>}
          </div>
        </div>

        {/* Editor form */}
        <div>
          {!form ? (
            <div className="adm-card text-center py-20">
              <p className="font-heading text-2xl text-ink/30">Select a section to edit</p>
              <p className="font-body text-sm text-ink/50 mt-2">or click “New Section” to add one.</p>
            </div>
          ) : (
            <div className="adm-card space-y-6" data-testid="guide-editor-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="adm-label">Section title</label>
                  <input className="adm-input" value={form.label} onChange={(e) => set("label", e.target.value)} data-testid="guide-field-label" />
                </div>
                <div>
                  <label className="adm-label">“Open” link (admin path)</label>
                  <input className="adm-input" placeholder="/admin/orders" value={form.route} onChange={(e) => set("route", e.target.value)} data-testid="guide-field-route" />
                </div>
              </div>

              <div>
                <label className="adm-label">Visible to roles</label>
                <div className="flex flex-wrap gap-2">
                  {ROLES.map((r) => (
                    <button key={r.id} type="button" onClick={() => toggleRole(r.id)}
                      className={`px-3 py-1.5 font-body text-[0.65rem] uppercase tracking-wide2 border transition-colors ${form.roles.includes(r.id) ? "bg-ink text-linen border-ink" : "border-ink/20 text-ink/60 hover:border-ink/40"}`}
                      data-testid={`guide-role-${r.id}`}>
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="adm-label">Summary</label>
                <textarea rows={3} className="adm-input resize-y" value={form.summary} onChange={(e) => set("summary", e.target.value)} data-testid="guide-field-summary" />
              </div>

              <div>
                <label className="adm-label">Screenshot</label>
                <p className="font-body text-[0.6rem] text-ink/50 mb-2">Recommended: 1500×1000 · 3:2 landscape (a clean capture of the page)</p>
                <div className="flex gap-4 items-start">
                  <div className="w-40 h-28 bg-cream border border-ink/10 overflow-hidden shrink-0 flex items-center justify-center">
                    {form.image ? <img src={imgSrc(form.image)} alt="" className="w-full h-full object-cover object-top" /> : <span className="text-ink/30 text-xs">No image</span>}
                  </div>
                  <div className="flex-1 space-y-2">
                    <label className="adm-btn-outline w-fit cursor-pointer">
                      <Upload size={13} /> {uploading ? "Uploading…" : "Upload screenshot"}
                      <input type="file" accept="image/*" className="hidden" onChange={uploadImg} data-testid="guide-image-file" />
                    </label>
                    <input className="adm-input" placeholder="…or paste an image URL" value={form.image} onChange={(e) => set("image", e.target.value)} data-testid="guide-field-image" />
                    {form.image && <button type="button" onClick={() => set("image", "")} className="text-[0.6rem] uppercase tracking-wide2 text-destructive flex items-center gap-1"><X size={11} /> Remove</button>}
                  </div>
                </div>
              </div>

              <ListField title="Steps" items={form.steps} onChange={(v) => set("steps", v)} testId="guide-step" />
              <ListField title="Tips" items={form.tips} onChange={(v) => set("tips", v)} testId="guide-tip" />

              <div className="flex justify-end pt-2 border-t border-ink/10">
                <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-ink text-linen px-6 py-3 font-body text-[0.62rem] uppercase tracking-wide2 hover:bg-ink/90 disabled:opacity-50 transition-colors" data-testid="guide-save">
                  <Save size={14} /> {saving ? "Saving…" : selId === "new" ? "Create Section" : "Save Changes"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
